import { store } from '../store';
import {
  acknowledgeParticipants,
  clearConversation,
  restoreEntryHistory,
  restoreTranscriptHistory,
  setParticipants,
} from '../store/conversationSlice';
import { clearLandmineAlerts, restoreLandmineHistory } from '../store/slices/landmineSlice';
import { clearCompetitorAlerts, restoreCompetitorHistory } from '../store/slices/competitorSlice';
import { fetchSessionState } from './sessionStateApi';
import { sessionManager } from './sessionManager';
import type { SessionStateData } from '../types/sessionState';
import {
  toHistoryCompetitorAlerts,
  toHistoryEntries,
  toHistoryLandmines,
  toHistoryRoster,
  toHistoryTranscripts,
} from '../utils/sessionHistory';

/**
 * The live call's data in the store: clearing it between sessions, and the
 * SILENT history restore (owner decision 2026-10-03).
 *
 * The restore runs when a watcher joins, after every reconnect of the recall
 * or standalone session socket, and when the owner resumes: the saved
 * session's roster, transcript lines, cards and alerts, each marked
 * `isRestored` (shown muted as "Earlier", no toast, sound, flash or
 * highlight) and de-duplicated against what the live sockets already
 * delivered (the saver lags the live frames by seconds). Speech-rate alerts
 * are not restored: their only display is a banner, which is not silent.
 */

/** Empty the transcript, cards and alerts before another session fills them. */
export const clearLiveSessionData = (): void => {
  store.dispatch(clearConversation());
  store.dispatch(clearLandmineAlerts());
  store.dispatch(clearCompetitorAlerts());
};

const applySessionHistory = (state: SessionStateData): void => {
  const roster = toHistoryRoster(state.participants);
  if (roster.length > 0) {
    store.dispatch(setParticipants(roster));
    // Already on the call: the roles modal must not re-open for them.
    store.dispatch(acknowledgeParticipants());
  }
  store.dispatch(restoreTranscriptHistory(toHistoryTranscripts(state.transcripts)));
  store.dispatch(restoreEntryHistory(toHistoryEntries(state)));
  store.dispatch(restoreLandmineHistory(toHistoryLandmines(state.landmineDetections)));
  store.dispatch(restoreCompetitorHistory(toHistoryCompetitorAlerts(state.competitorDetections)));
};

/**
 * Load `sessionId`'s saved call and add it to the store as history. Returns
 * the saved state (callers read `isLive`), or null when there is none yet or
 * it could not be read; a failure is logged and leaves the live view as it is.
 * A response for a session this page has since left is dropped.
 */
export const restoreSessionHistory = async (sessionId: string): Promise<SessionStateData | null> => {
  if (!sessionId) return null;
  try {
    const state = await fetchSessionState(sessionId);
    if (!state) return null;
    if (sessionManager.getRoomId() !== sessionId) {
      console.info(`[liveSessionData] Dropped history for ${sessionId}: the page moved to another session`);
      return null;
    }
    applySessionHistory(state);
    return state;
  } catch (error) {
    console.warn(`[liveSessionData] Could not restore the history of ${sessionId}:`, error);
    return null;
  }
};
